import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  listMeituanOrderFulfillLogs,
  type MeituanOrderFulfillLog as MeituanOrderFulfillLogRecord,
} from '../services/meituanOrderFulfillLogService';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const statusMap: Record<string, { text: string; color: string }> = {
  pending: { text: '待处理', color: 'default' },
  success: { text: '成功', color: 'green' },
  failed: { text: '失败', color: 'red' },
};

const fulfillStatusMap: Record<number, { text: string; color: string }> = {
  40001: { text: '已分配', color: 'blue' },
  40002: { text: '已到达', color: 'cyan' },
  41001: { text: '履约完成', color: 'green' },
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value;
};

const formatJSON = (value?: string) => {
  if (!value) return '-';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const MeituanOrderFulfillLog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<MeituanOrderFulfillLogRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [serviceOrderNo, setServiceOrderNo] = useState('');
  const [meituanOrderId, setMeituanOrderId] = useState('');
  const [appOrderId, setAppOrderId] = useState('');
  const [shopId, setShopId] = useState('');
  const [orderFulfillStatus, setOrderFulfillStatus] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentLog, setCurrentLog] = useState<MeituanOrderFulfillLogRecord | null>(null);

  const loadData = async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await listMeituanOrderFulfillLogs({
        page: nextPage,
        pageSize: nextPageSize,
        serviceOrderNo: serviceOrderNo || undefined,
        meituanOrderId: meituanOrderId || undefined,
        appOrderId: appOrderId || undefined,
        shopId: shopId || undefined,
        orderFulfillStatus,
        status,
        startTime: dateRange?.[0]?.format('YYYY-MM-DD 00:00:00'),
        endTime: dateRange?.[1]?.format('YYYY-MM-DD 23:59:59'),
      });
      setLogs(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载美团履约日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    void loadData(1, pageSize);
  };

  const handleReset = () => {
    setServiceOrderNo('');
    setMeituanOrderId('');
    setAppOrderId('');
    setShopId('');
    setOrderFulfillStatus(undefined);
    setStatus(undefined);
    setDateRange(null);
    setPage(1);
    setTimeout(() => {
      void loadData(1, pageSize);
    }, 0);
  };

  const openDetail = (record: MeituanOrderFulfillLogRecord) => {
    setCurrentLog(record);
    setDetailVisible(true);
  };

  const columns: ColumnsType<MeituanOrderFulfillLogRecord> = [
    {
      title: '平台订单号',
      dataIndex: 'serviceOrderNo',
      key: 'serviceOrderNo',
      width: 170,
      ellipsis: true,
      render: value => value || '-',
    },
    {
      title: '美团订单ID',
      dataIndex: 'meituanOrderId',
      key: 'meituanOrderId',
      width: 180,
      ellipsis: true,
      render: value => (
        <Tooltip title={value || '-'}>
          <Text>{value || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '三方订单号',
      dataIndex: 'appOrderId',
      key: 'appOrderId',
      width: 190,
      ellipsis: true,
      render: value => value || '-',
    },
    {
      title: '门店/授权实体',
      dataIndex: 'shopId',
      key: 'shopId',
      width: 220,
      ellipsis: true,
      render: value => (
        <Tooltip title={value || '-'}>
          <Text>{value || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '履约状态',
      dataIndex: 'orderFulfillStatus',
      key: 'orderFulfillStatus',
      width: 120,
      render: value => {
        const item = fulfillStatusMap[value] || { text: value || '-', color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '调用状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: value => {
        const item = statusMap[value] || { text: value || '-', color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '错误信息',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      width: 240,
      ellipsis: true,
      render: value => value ? (
        <Tooltip title={value}>
          <Text type="danger">{value}</Text>
        </Tooltip>
      ) : '-',
    },
    {
      title: '成功时间',
      dataIndex: 'fulfillmentSyncedAt',
      key: 'fulfillmentSyncedAt',
      width: 170,
      render: value => formatDateTime(value),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: value => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => openDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <Title level={2}>美团履约日志</Title>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="平台订单号"
          allowClear
          value={serviceOrderNo}
          onChange={e => setServiceOrderNo(e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          placeholder="美团订单ID"
          allowClear
          value={meituanOrderId}
          onChange={e => setMeituanOrderId(e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          placeholder="三方订单号"
          allowClear
          value={appOrderId}
          onChange={e => setAppOrderId(e.target.value)}
          style={{ width: 180 }}
        />
        <Input
          placeholder="门店/授权实体"
          allowClear
          value={shopId}
          onChange={e => setShopId(e.target.value)}
          style={{ width: 180 }}
        />
        <Select
          placeholder="履约状态"
          allowClear
          value={orderFulfillStatus}
          onChange={setOrderFulfillStatus}
          style={{ width: 150 }}
          options={[
            { label: '已分配', value: '40001' },
            { label: '已到达', value: '40002' },
            { label: '履约完成', value: '41001' },
          ]}
        />
        <Select
          placeholder="调用状态"
          allowClear
          value={status}
          onChange={setStatus}
          style={{ width: 130 }}
          options={[
            { label: '成功', value: 'success' },
            { label: '失败', value: 'failed' },
            { label: '待处理', value: 'pending' },
          ]}
        />
        <RangePicker value={dateRange} onChange={setDateRange} />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          查询
        </Button>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>
          重置
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={logs}
        scroll={{ x: 1700 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: value => `共 ${value} 条`,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
      />

      <Modal title="美团履约日志详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null} width={900}>
        {currentLog && (
          <Space orientation="vertical" style={{ width: '100%' }} size="large">
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="平台订单ID">{currentLog.serviceOrderId || '-'}</Descriptions.Item>
              <Descriptions.Item label="平台订单号">{currentLog.serviceOrderNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="美团订单ID">{currentLog.meituanOrderId || '-'}</Descriptions.Item>
              <Descriptions.Item label="三方订单号">{currentLog.appOrderId || '-'}</Descriptions.Item>
              <Descriptions.Item label="门店/授权实体">{currentLog.shopId || '-'}</Descriptions.Item>
              <Descriptions.Item label="履约状态">
                {fulfillStatusMap[currentLog.orderFulfillStatus]?.text || currentLog.orderFulfillStatus || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="调用状态">{statusMap[currentLog.status]?.text || currentLog.status || '-'}</Descriptions.Item>
              <Descriptions.Item label="成功时间">{formatDateTime(currentLog.fulfillmentSyncedAt)}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{formatDateTime(currentLog.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="更新时间">{formatDateTime(currentLog.updatedAt)}</Descriptions.Item>
              <Descriptions.Item label="错误信息" span={2}>{currentLog.errorMessage || '-'}</Descriptions.Item>
              <Descriptions.Item label="授权Token" span={2}>
                <Text copyable ellipsis style={{ maxWidth: 760 }}>
                  {currentLog.appAuthToken || '-'}
                </Text>
              </Descriptions.Item>
            </Descriptions>
            <div>
              <Text strong>请求参数</Text>
              <pre style={{ marginTop: 8, maxHeight: 260, overflow: 'auto', background: '#f6f8fa', padding: 12 }}>
                {formatJSON(currentLog.requestPayload)}
              </pre>
            </div>
            <div>
              <Text strong>响应参数</Text>
              <pre style={{ marginTop: 8, maxHeight: 260, overflow: 'auto', background: '#f6f8fa', padding: 12 }}>
                {formatJSON(currentLog.responsePayload)}
              </pre>
            </div>
          </Space>
        )}
      </Modal>
    </Card>
  );
};

export default MeituanOrderFulfillLog;
